import { ArpMedia, ArpMediaType, ORDER } from '@arpeggi/shared'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { useStatus } from '../../../core/Utils'
import { useSearch } from './useSearch'

export interface SongsFilters {
  type: ArpMediaType[]
  key: number
  genre: string[]
  orderBy: keyof Omit<ArpMedia, 'metadata' | 'subComponents' | 'user'>
  subComponentId?: number[]
  order: ORDER
  walletAddress?: string
  featured?: boolean
  shadowBlocklist?: boolean
  excludeIds?: number[]
}

export const DEFAULT_TYPES: ArpMediaType[] = [ArpMediaType.SONG]
export const DEFAULT_GENRES: string[] = []
export const DEFAULT_ORDER_BY: keyof Omit<ArpMedia, 'metadata' | 'subComponents' | 'user'> =
  'createdAt'
export const DEFAULT_ORDER: ORDER = 'desc'
export const DEFAULT_KEY: number | undefined = undefined
export const DEFAULT_WALLET_ADDRESS: string | undefined = undefined
export const DEFAULT_FEATURED_MEDIA: boolean | undefined = undefined

export const DEFAULT_FILTERS: SongsFilters = {
  key: DEFAULT_KEY,
  type: DEFAULT_TYPES,
  genre: DEFAULT_GENRES,
  orderBy: DEFAULT_ORDER_BY,
  order: DEFAULT_ORDER,
  walletAddress: DEFAULT_WALLET_ADDRESS,
  featured: DEFAULT_FEATURED_MEDIA
}

const useFilters = (defaultFilters = DEFAULT_FILTERS) => {
  const [filters, setFilters] = useState<SongsFilters>(defaultFilters)
  const updateFilters = (filter: Partial<SongsFilters>) => setFilters({ ...filters, ...filter })
  return { filters, updateFilters }
}

const useSongs = (defaultFilters = DEFAULT_FILTERS) => {
  const [search] = useSearch()
  const { filters, updateFilters } = useFilters(defaultFilters)
  const [data, setData] = useState<IArpMedia[]>([])
  const [status, setStatus] = useStatus()
  const [pageData, setPageData] = useState<{ currentPage: number; lastPage: number }>({
    currentPage: 0,
    lastPage: 1
  })
  const hasMore = pageData.currentPage < pageData.lastPage

  const handleSearch = async (filters: SongsFilters, search: string, fresh = false) => {
    const page = fresh ? 1 : pageData.currentPage + 1
    const { media, meta } = await ArpRegistry.search({ search, ...filters, page })
    const { lastPage, currentPage } = meta
    setPageData({ currentPage, lastPage })
    setData(fresh ? media : [...data, ...media])
    setStatus('ready')
  }

  const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [])

  useEffect(() => {
    setStatus('loading')
    debouncedSearch(filters, search, true)
  }, [filters, search])

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  const getNextPage = () => handleSearch(filters, search)

  return { updateFilters, filters, hasMore, data, getNextPage, status }
}

export const noSongFilters = (filters: SongsFilters) => {
  if (filters.key !== DEFAULT_KEY) return false
  if (filters.genre !== DEFAULT_GENRES) return false
  return true
}

export default useSongs
